import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import Link from '@material-ui/core/Link';
import { url } from '../../reducers/url';

const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1,
        width: '96%',
        margin: '0 auto 28px',
        minHeight: '260px',
        padding: '23px 16px 14px'
    },
    tocRow: {
        alignItems: 'center',
        marginBottom: 16
    },
    section: {
        padding: '16px 0',
        // marginTop: 8,
        scrollMarginTop: 72,
    },
    heading: {
        fontWeight: 600,
        marginBottom: 8,

    },
    loader: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: '999999',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'block',
    },
    loaderPosition: {
        marginLeft: '602px',
        marginTop: '300px',
    },
}));

const handleClickHelper = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Minimal HTML sanitization to prevent XSS attacks
const sanitizeHtml = (html) => {
    if (!html) return '';
    
    // Use DOMParser to safely parse HTML without executing scripts
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove script tags completely
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove potentially dangerous tags
    const dangerousTags = ['iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select', 'option'];
    dangerousTags.forEach(tagName => {
        const elements = doc.querySelectorAll(tagName);
        elements.forEach(el => el.remove());
    });
    
    // Remove dangerous event handler attributes
    const dangerousAttrs = [
        'onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout', 'onfocus', 'onblur',
        'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress', 'onmousedown',
        'onmouseup', 'onmousemove', 'ondblclick', 'oncontextmenu', 'onwheel'
    ];
    
    const allElements = doc.querySelectorAll('*');
    allElements.forEach(el => {
        dangerousAttrs.forEach(attr => {
            if (el.hasAttribute(attr)) {
                el.removeAttribute(attr);
            }
        });
        
        // Sanitize href attributes to prevent javascript: URLs
        if (el.hasAttribute('href')) {
            const href = el.getAttribute('href');
            if (href && (href.toLowerCase().startsWith('javascript:') || href.toLowerCase().startsWith('data:'))) {
                el.removeAttribute('href');
            }
        }
        
        // Sanitize src attributes
        if (el.hasAttribute('src')) {
            const src = el.getAttribute('src');
            if (src && (src.toLowerCase().startsWith('javascript:') || src.toLowerCase().startsWith('data:'))) {
                el.removeAttribute('src');
            }
        }
    });
    
    // Safely return the sanitized HTML using the body's innerHTML
    // DOMParser's parsed document is safe at this point
    return doc.body ? doc.body.innerHTML : '';
};

function PatentStatistics() {
    const classes = useStyles();
    const { workflowId } = useParams();
    const [rawHtml, setRawHtml] = useState('');
    const [gsHtml, setGsHtml] = useState('');
    const [ndpqHtml, setNdpqHtml] = useState('');
    const [ndbaHtml, setNdbaHtml] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    
    useEffect(() => {
        const run = async () => {
            try {
                if (!workflowId) return;
                setShowLoader(true); // Show loader when starting the fetch
                const base = process.env.REACT_APP_API_URL;
                const pattern = url.patentStats;
                const apiUrl = (base + pattern).replace('**', encodeURIComponent(workflowId));
                const res = await fetch(apiUrl, { method: 'GET', credentials: 'include' });
                const ct = res.headers.get('content-type') || '';
                if (!res.ok) {
                    const body = await res.text();
                    console.error('HTTP error', res.status, res.statusText, body);
                    setShowLoader(false); // Hide loader on error
                    return;
                }
                if (ct.includes('application/json')) {
                    const json = await res.json();
                    const htmlString = json.response_content || '';
                    setRawHtml(htmlString);
                    setGsHtml(extractSectionByAnchor(htmlString, 'GS'));
                    setNdpqHtml(extractSectionByAnchor(htmlString, 'NDPQ'));
                    setNdbaHtml(extractSectionByAnchor(htmlString, 'NDBA'));
                    console.log('JSON:', json);
                } else {
                    const html = await res.text();
                    console.log('HTML:', html);
                    setGsHtml(extractSectionByAnchor(html, 'GS'));    // Global Statistics
                    setNdpqHtml(extractSectionByAnchor(html, 'NDPQ')); // Number of documents per query
                    setNdbaHtml(extractSectionByAnchor(html, 'NDBA')); // Number of documents by authority
                }
                setShowLoader(false); // Hide loader when done
            } catch (err) {
                console.error('Fetch error:', err);
                setShowLoader(false); // Hide loader on error
            }
        };
        run();
    }, [workflowId]);

    function extractSectionByAnchor(htmlString, anchorName) {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html'); 
        const anchor = doc.querySelector(`h2 a[name="${anchorName}"]`); 
        if (!anchor) return '';
        const startH2 = anchor.closest('h2');
        let node = startH2.nextSibling;
        
        // Build safe HTML by recreating elements with only safe content
        const safeElements = [];

        while (node) {
            if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'h2') break; 
            if (node.nodeType === Node.ELEMENT_NODE) {
                // Create safe element by rebuilding with only safe attributes
                const safePage = buildSafeElement(node);
                if (safePage) safeElements.push(safePage);
            } else if (node.nodeType === Node.TEXT_NODE) {
                // Text nodes are safe - escape for HTML
                const text = node.textContent;
                if (text && text.trim()) {
                    safeElements.push(escapeHtml(text));
                }
            }
            node = node.nextSibling;
        }

        let result = safeElements.join('');
        
        if (anchorName === 'NDBA') {
            // Remove copyright by string manipulation (safe)
            result = result.replace(/<center[^>]*id\s*=\s*["']copyright["'][^>]*>.*?<\/center>/gi, '');
        }
        return result;
    }

    // Build safe HTML element with only allowed tags and attributes
    function buildSafeElement(element) {
        const tagName = element.tagName.toLowerCase();
        const allowedTags = ['table', 'tr', 'td', 'th', 'tbody', 'thead', 'tfoot', 'p', 'div', 'span', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'b', 'strong', 'i', 'em', 'center', 'a'];
        
        if (!allowedTags.includes(tagName)) return '';
        
        const allowedAttrs = ['class', 'id', 'colspan', 'rowspan', 'align', 'href', 'target'];
        const attrs = [];
        
        // Build safe attributes
        for (const attr of allowedAttrs) {
            if (element.hasAttribute(attr)) {
                const value = element.getAttribute(attr);
                if (value && !value.toLowerCase().startsWith('javascript:') && !value.toLowerCase().startsWith('data:')) {
                    attrs.push(`${attr}="${escapeHtml(value)}"`);
                }
            }
        }
        
        const attrString = attrs.length > 0 ? ' ' + attrs.join(' ') : '';
        const textContent = element.textContent || '';
        
        // Self-closing tags
        if (['br', 'hr'].includes(tagName)) {
            return `<${tagName}${attrString} />`;
        }
        
        // Container tags - recursively process children
        let childrenHtml = '';
        for (const child of element.childNodes) {
            if (child.nodeType === Node.ELEMENT_NODE) {
                childrenHtml += buildSafeElement(child);
            } else if (child.nodeType === Node.TEXT_NODE) {
                childrenHtml += escapeHtml(child.textContent);
            }
        }
        
        return `<${tagName}${attrString}>${childrenHtml}</${tagName}>`;
    }

    // HTML escape function
    function escapeHtml(text) {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;');
    }

    const newUrl = process.env.REACT_APP_API_URL + 'do=gqresult&template=resultsumm&group=s.pn&nbingroup=1&db=wf'+encodeURIComponent(workflowId)+'.patent_statistics_tmp.coll&filter=session';

    const patchedHtml = sanitizeHtml(ndpqHtml.replace(
        /<a\s+onclick=["'][^"']*goto_resdb\('([^']+)','[^']*'\)["'][^>]*>(\d+)<\/a>/g,
        (match, _url, count) => `<a href="${newUrl}" target="_blank">${count}</a>`
    ));


    return (
        <div className={classes.grow}>
            <h2>Table of Contents</h2>

            <div className={classes.tocRow}>
                <div>
                    <Link href="#GS" onClick={handleClickHelper('GS')}>Global Statistics</Link>
                </div>
                <div>
                    <Link href="#NDBA" onClick={handleClickHelper('NDBA')}>Number of documents by authority</Link>
                </div>
                <div>
                    <Link href="#NDPQ" onClick={handleClickHelper('NDPQ')}>Number of documents per query</Link>
                </div>
            </div>
            <section id="GS" className={classes.section}>
                <div className={classes.heading}>Global Statistics</div>
                <div style={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(gsHtml) }} />
            </section>

            <section id="NDBA" className={classes.section}>
                <div className={classes.heading}>Number of documents by authority</div>
                <div className='ndpqScope' style={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(ndbaHtml) }} />
            </section>

            <section id="NDPQ" className={classes.section}>
                <div className={classes.heading}>Number of documents per query</div>
                {console.log('ndpqHtml',ndpqHtml)}
                <div className='ndpqScope' style={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: patchedHtml }} />
            </section>

            {showLoader &&
                <div className={classes.loader}>
                    <div className={classes.loaderPosition}>
                        <div className="spinner-container">
                            <div className="spinner-socket">
                                <div className="spinner-hex spinner-tile-1">
                                    <div className="spinner-brick rotate-0"></div>
                                    <div className="spinner-brick rotate-1"></div>
                                    <div className="spinner-brick rotate-2"></div>
                                </div>
                                <div className="spinner-hex spinner-tile-2">
                                    <div className="spinner-brick rotate-0"></div>
                                    <div className="spinner-brick rotate-1"></div>
                                    <div className="spinner-brick rotate-2"></div>
                                </div>
                                <div className="spinner-hex spinner-tile-3 spinner-row-2">
                                    <div className="spinner-brick rotate-0"></div>
                                    <div className="spinner-brick rotate-1"></div>
                                    <div className="spinner-brick rotate-2"></div>
                                </div>
                                <div className="spinner-hex spinner-tile-4 spinner-row-2">
                                    <div className="spinner-brick rotate-0"></div>
                                    <div className="spinner-brick rotate-1"></div>
                                    <div className="spinner-brick rotate-2"></div>
                                </div>
                                <div className="spinner-hex spinner-tile-5 spinner-row-2">
                                    <div className="spinner-brick rotate-0"></div>
                                    <div className="spinner-brick rotate-1"></div>
                                    <div className="spinner-brick rotate-2"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
}

export default PatentStatistics;
